import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Input, Card, Typography, Tag,
  message, Row, Col, Tooltip, Popconfirm,
} from 'antd';
import {
  PlusOutlined, EditOutlined, FileDoneOutlined, ShopOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const { Title } = Typography;
const { Search } = Input;

const SatilikListe = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/satilik');
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setData(arr);
    } catch {
      message.error('Satılık işlemler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/satilik/${id}`);
      message.success('Kayıt silindi');
      fetchData();
    } catch {
      message.error('Silme başarısız');
    }
  };

  const formatMoney = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
  };

  const filtered = data.filter((item) => {
    const q = searchText.toLowerCase();
    return (
      (item.saticiAdi || '').toLowerCase().includes(q) ||
      (item.aliciAdi || '').toLowerCase().includes(q) ||
      (item.ilAdi || '').toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: 'Satıcı',
      dataIndex: 'saticiAdi',
      key: 'saticiAdi',
      render: (t) => <strong>{t || '-'}</strong>,
    },
    {
      title: 'Alıcı',
      dataIndex: 'aliciAdi',
      key: 'aliciAdi',
    },
    {
      title: 'İl / İlçe',
      key: 'adres',
      render: (_, r) => `${r.ilAdi || '-'} / ${r.ilceAdi || '-'}`,
    },
    {
      title: 'Ana Para',
      dataIndex: 'anapara',
      key: 'anapara',
      render: (v) => <span className="money-value">{formatMoney(v)}</span>,
    },
    {
      title: 'Peşinat',
      dataIndex: 'pesinat',
      key: 'pesinat',
      render: (v) => formatMoney(v),
    },
    {
      title: 'Kalan',
      dataIndex: 'kalan',
      key: 'kalan',
      render: (v) => formatMoney(v),
    },
    {
      title: 'Oluşturan',
      dataIndex: 'kayitKullanici',
      key: 'kayitKullanici',
      render: (v) => v || '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Düzenle">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/satilik/${record.id}/duzenle`)}
            />
          </Tooltip>
          <Tooltip title="Sözleşme">
            <Button
              size="small"
              icon={<FileDoneOutlined />}
              onClick={() => navigate(`/satis-sozlesmesi/${record.id}`)}
            >
              Sözleşme
            </Button>
          </Tooltip>
          <Tooltip title="Sil">
            <Popconfirm
              title="Bu kaydı silmek istiyor musunuz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <ShopOutlined style={{ fontSize: 20, color: '#1677ff' }} />
            <Title level={4} style={{ margin: 0 }}>Satılık İşlemler</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Search
              placeholder="Ara..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/satilik/yeni')}>
              Yeni Satılık Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      <Card className="content-card" bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          bordered
          size="middle"
          scroll={{ x: 900 }}
          pagination={{ pageSize: 15, showTotal: (t) => `Toplam ${t} kayıt`, showSizeChanger: true }}
          locale={{ emptyText: 'Satılık işlem bulunamadı' }}
        />
      </Card>
    </div>
  );
};

export default SatilikListe;
